import { v4 as uuid } from "uuid";
/**
 * Scout peer + interest manager — ported from kritaka-electron's
 * `renderer/services/scout-peer-manager.ts`. Module-level singleton that
 * owns, per scout instance:
 *
 * 1. The `viewer_interest` heartbeat Scout uses to decide whether to keep
 *    publishing (15s cadence while view is active, 60s grace after unmount).
 * 2. The RTCPeerConnection itself. Lifted out of the React tree so switching
 *    scouts does not tear down the peer mid-grace.
 *
 * Hub interactions go through the injected `HubClient` (web: direct fetch to
 * api.kritaka.io; Desktop will inject an IPC-backed client when this module
 * is ported to packages/ui).
 */
const HEARTBEAT_INTERVAL_MS = 15_000;
const VIEW_GRACE_MS = 60_000;
const CLIENT_SESSION_KEY = "kritaka:clientSessionId";
function loadClientSessionId() {
    let id = localStorage.getItem(CLIENT_SESSION_KEY);
    if (!id) {
        id = uuid();
        localStorage.setItem(CLIENT_SESSION_KEY, id);
    }
    return id;
}
let hub = null;
const entries = new Map();
let clientSessionId = null;
export function configurePeerManager(client) {
    hub = client;
}
function requireHub() {
    if (!hub) {
        throw new Error("scout peer-manager used before configurePeerManager()");
    }
    return hub;
}
function sessionId() {
    if (!clientSessionId)
        clientSessionId = loadClientSessionId();
    return clientSessionId;
}
function publicState(e) {
    return {
        mediaStream: e.mediaStream,
        sessionId: e.sessionId,
        error: e.error,
        connecting: e.connecting,
    };
}
function notify(e) {
    const snapshot = publicState(e);
    for (const cb of e.subscribers)
        cb(snapshot);
}
function getOrCreate(instanceId) {
    let e = entries.get(instanceId);
    if (!e) {
        e = {
            viewActiveNow: false,
            lastSeenAt: 0,
            heartbeatTimer: null,
            peer: null,
            viewerId: null,
            subscribers: new Set(),
            negotiatingFor: null,
            mediaStream: null,
            sessionId: null,
            error: null,
            connecting: false,
        };
        entries.set(instanceId, e);
    }
    return e;
}
function isInterested(e) {
    return e.viewActiveNow || Date.now() - e.lastSeenAt < VIEW_GRACE_MS;
}
function sendHeartbeat(instanceId) {
    const sid = sessionId();
    void requireHub()
        .postViewerInterest(instanceId, sid)
        .then((res) => {
        console.log("[scout-peer] viewer-interest sent", instanceId.slice(0, 8), sid.slice(0, 8));
        setSessionId(instanceId, res.active_session_id);
    })
        .catch((err) => {
        console.warn("[scout-peer] viewer-interest threw", instanceId, err);
    });
}
function tickHeartbeat(instanceId) {
    const e = entries.get(instanceId);
    if (!e)
        return;
    if (!isInterested(e)) {
        stopHeartbeat(instanceId);
        teardownPeer(instanceId, "grace expired");
        if (!e.subscribers.size)
            entries.delete(instanceId);
        return;
    }
    sendHeartbeat(instanceId);
    reconcile(instanceId);
}
function startHeartbeat(instanceId) {
    const e = entries.get(instanceId);
    if (!e || e.heartbeatTimer !== null)
        return;
    sendHeartbeat(instanceId);
    e.heartbeatTimer = window.setInterval(() => tickHeartbeat(instanceId), HEARTBEAT_INTERVAL_MS);
}
function stopHeartbeat(instanceId) {
    const e = entries.get(instanceId);
    if (!e || e.heartbeatTimer === null)
        return;
    window.clearInterval(e.heartbeatTimer);
    e.heartbeatTimer = null;
}
const FALLBACK_ICE_SERVERS = [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun.cloudflare.com:3478" },
];
async function fetchIceServers() {
    try {
        const servers = await requireHub().getViewerIceServers();
        if (servers.length > 0)
            return servers;
        console.warn("[scout-peer] viewer-ice-servers empty, using STUN-only fallback");
    }
    catch (err) {
        console.warn("[scout-peer] viewer-ice-servers threw, using STUN-only fallback", err);
    }
    return FALLBACK_ICE_SERVERS;
}
async function waitForIceGathering(pc, timeoutMs) {
    if (pc.iceGatheringState === "complete")
        return;
    await new Promise((resolve) => {
        const timer = window.setTimeout(() => {
            pc.removeEventListener("icegatheringstatechange", onChange);
            resolve();
        }, timeoutMs);
        const onChange = () => {
            if (pc.iceGatheringState === "complete") {
                window.clearTimeout(timer);
                pc.removeEventListener("icegatheringstatechange", onChange);
                resolve();
            }
        };
        pc.addEventListener("icegatheringstatechange", onChange);
    });
}
async function negotiatePeer(instanceId, sid) {
    const e = entries.get(instanceId);
    if (!e)
        return;
    if (e.peer && e.negotiatingFor !== sid)
        teardownPeer(instanceId, "session changed");
    if (e.peer)
        return;
    e.negotiatingFor = sid;
    e.connecting = true;
    e.error = null;
    notify(e);
    const iceServers = await fetchIceServers();
    const afterFetch = entries.get(instanceId);
    if (!afterFetch || afterFetch.negotiatingFor !== sid)
        return;
    const pc = new RTCPeerConnection({ iceServers });
    e.peer = pc;
    pc.ontrack = (ev) => {
        const cur = entries.get(instanceId);
        if (!cur || cur.peer !== pc)
            return;
        console.log("[scout-peer] ontrack fired", instanceId.slice(0, 8), ev.streams.length);
        // Multi-track ontrack hazard (KTK-180): audio + video arrive as separate
        // events; the second can have an empty streams[]. Preserve any existing
        // stream instead of clobbering to null.
        if (ev.streams[0])
            cur.mediaStream = ev.streams[0];
        else if (cur.mediaStream)
            cur.mediaStream.addTrack(ev.track);
        else
            cur.mediaStream = new MediaStream([ev.track]);
        cur.connecting = false;
        notify(cur);
    };
    pc.oniceconnectionstatechange = () => {
        console.log("[scout-peer] ICE state", instanceId.slice(0, 8), pc.iceConnectionState);
    };
    pc.onconnectionstatechange = () => {
        console.log("[scout-peer] peer state", instanceId.slice(0, 8), pc.connectionState);
    };
    pc.addTransceiver("video", { direction: "recvonly" });
    pc.addTransceiver("audio", { direction: "recvonly" });
    try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        if (!offer.sdp)
            throw new Error("empty SDP offer");
        await waitForIceGathering(pc, 5000);
        const cur = entries.get(instanceId);
        if (!cur || cur.peer !== pc || cur.negotiatingFor !== sid) {
            pc.close();
            return;
        }
        const fullSdp = pc.localDescription?.sdp ?? offer.sdp;
        const res = await requireHub().joinScoutStream(sid, fullSdp);
        const cur2 = entries.get(instanceId);
        if (!cur2 || cur2.peer !== pc || cur2.negotiatingFor !== sid) {
            pc.close();
            if (res.sdp_answer && res.viewer_id) {
                void requireHub().leaveScoutStream(sid, res.viewer_id);
            }
            return;
        }
        if (!res.sdp_answer || !res.viewer_id) {
            console.warn("[scout-peer] joinScoutStream !ok", {
                instanceId: instanceId.slice(0, 8),
                sid: sid.slice(0, 8),
                hasAnswer: !!res.sdp_answer,
                hasViewerId: !!res.viewer_id,
            });
            cur2.error = "Failed to join stream";
            cur2.connecting = false;
            cur2.peer = null;
            pc.close();
            notify(cur2);
            return;
        }
        cur2.viewerId = res.viewer_id;
        await pc.setRemoteDescription({ type: "answer", sdp: res.sdp_answer });
        console.log("[scout-peer] peer negotiated", instanceId.slice(0, 8), sid.slice(0, 8));
    }
    catch (err) {
        const message = err instanceof Error ? err.message : "Stream join failed";
        console.warn("[scout-peer] negotiatePeer threw", instanceId.slice(0, 8), sid.slice(0, 8), err);
        const cur = entries.get(instanceId);
        if (cur && cur.peer === pc) {
            cur.error = message;
            cur.connecting = false;
            cur.peer = null;
            notify(cur);
        }
        pc.close();
    }
}
function teardownPeer(instanceId, _reason) {
    const e = entries.get(instanceId);
    if (!e)
        return;
    if (e.peer) {
        const sid = e.negotiatingFor;
        const vid = e.viewerId;
        try {
            e.peer.close();
        }
        catch { /* ignore */ }
        if (sid && vid)
            void requireHub().leaveScoutStream(sid, vid);
    }
    e.peer = null;
    e.viewerId = null;
    e.mediaStream = null;
    e.negotiatingFor = null;
    e.connecting = false;
    notify(e);
}
function reconcile(instanceId) {
    const e = entries.get(instanceId);
    if (!e)
        return;
    if (!isInterested(e)) {
        teardownPeer(instanceId, "not interested");
        return;
    }
    if (!e.sessionId) {
        if (e.peer)
            teardownPeer(instanceId, "sessionId cleared");
        return;
    }
    if (e.peer && e.negotiatingFor !== e.sessionId) {
        teardownPeer(instanceId, "session changed");
    }
    if (!e.peer) {
        void negotiatePeer(instanceId, e.sessionId);
    }
}
export function setViewActive(instanceId) {
    const e = getOrCreate(instanceId);
    e.viewActiveNow = true;
    e.lastSeenAt = Date.now();
    startHeartbeat(instanceId);
    reconcile(instanceId);
}
export function setViewInactive(instanceId) {
    const e = entries.get(instanceId);
    if (!e)
        return;
    e.viewActiveNow = false;
    e.lastSeenAt = Date.now();
}
export function setSessionId(instanceId, sid) {
    const e = getOrCreate(instanceId);
    if (e.sessionId === sid)
        return;
    e.sessionId = sid;
    e.error = null;
    notify(e);
    reconcile(instanceId);
}
export function subscribe(instanceId, cb) {
    const e = getOrCreate(instanceId);
    e.subscribers.add(cb);
    cb(publicState(e));
    return () => {
        const cur = entries.get(instanceId);
        if (!cur)
            return;
        cur.subscribers.delete(cb);
        if (!cur.subscribers.size &&
            !cur.viewActiveNow &&
            cur.heartbeatTimer === null &&
            !cur.peer) {
            entries.delete(instanceId);
        }
    };
}
//# sourceMappingURL=peer-manager.js.map